import { ChoiceLocation } from './types'
import { ChoiceMultiprofile } from '../../components/ChoiceMultiprofile'
import { FunctionComponent, useCallback, useEffect, useState } from 'react'
import { HeroWrapper } from '@/styles/rebranded/HeroWrapper'
import { InactiveUser } from '@/modules/Activation/store/InactiveUserStore/types'
import { IS_PRODUCTION } from '@/constants'
import { ClinicAndFlags } from '@/modules/Auth/auth-types'
import { User } from '@/modules/Auth/classes/User'
import { getCache, removeCache, setCache } from '@/utils/cache'
import { setRefreshToken, setToken } from '@/utils/token'
import { useHistory, useLocation } from 'react-router'
import { toast } from '@conexasaude/conexa-hero'
import { useAuthContext } from '@/modules/Auth/contexts/AuthContext'
import { useFirebaseContext } from '@/modules/Firebase/contexts/FirebaseContext'
import { useInactiveUserStore } from '@/modules/Activation/store/InactiveUserStore/validation.store'
import { useMedicalSpecialitiesContext } from '@/modules/MedicalSpecialities/contexts/MedicalSpecialitiesContext'
import { useModalContext } from '@/components/RebrandedModal/contexts/ModalContext'
import { useRebrandingThemeGeneratorContext } from '@/rebrandedTheme/contexts/ThemeGeneratorContext'
import { useThemeGeneratorContext } from '@/theme/contexts/ThemeGeneratorContext'
import rebrandedThemeServices from '@/rebrandedTheme/theme.services'
import service from '../../auth-services'
import themeServices from '@/theme/theme.services'
import { RenderIf } from '@/components/RenderIf'
import LoadingPage from '@/components/LoadingPage'
import { TermsAndConditionsModal } from '../../components/TermsAndConditionsModal'
import { DependentsModal } from '@/modules/Activation/components/DependentsModal'
import { showErrorToast } from '@/modules/Activation/utils/requisitionErrors'

const Choice: FunctionComponent = () => {
  const history = useHistory()
  const location = useLocation<ChoiceLocation>()

  const { logAnalyticsEvent } = useFirebaseContext()
  const { setSpecialities } = useMedicalSpecialitiesContext()
  const { users, choiceUser, setUserClinic, signOut } = useAuthContext()
  const { setThemeColors } = useThemeGeneratorContext()
  const { setThemeColors: setRebrandingThemeColors } =
    useRebrandingThemeGeneratorContext()
  const { showModal } = useModalContext()

  const { setInactiveUser, setConfirmedData, setUserActivated } =
    useInactiveUserStore()

  const [activeTab, setActiveTab] = useState<string | null>('activeUsers')
  const [alertIsOpen, setAlertIsOpen] = useState(true)
  const [currentUser, setCurrentUser] = useState(null)
  const [currentProfileId, setCurrentProfileId] = useState(0)
  const [loading, setLoading] = useState(false)
  const [termsModalActive, setTermsModalActive] = useState(false)

  const redirectAfterChoice = useCallback(
    (pathname?: string) => {
      if (location?.state?.from) {
        history.push(location?.state?.from?.pathname)
        return
      }

      history.push(pathname)
    },
    [history, location?.state?.from],
  )

  const returnToLoginPage = useCallback(() => {
    logAnalyticsEvent('Pac_AtivacaoLogada_Voltar')
    signOut(true)
    if (location?.pathname === '/escolha-unificada') {
      if (IS_PRODUCTION) {
        window.location.href =
          'https://consultorioonline.psicologiaviva.com.br/login'
      } else {
        window.location.href =
          'https://hml-consultorioonline.psicologiaviva.com.br/login'
      }
    } else {
      history.push('/login')
    }
  }, [history, location?.pathname, logAnalyticsEvent, signOut])

  const setIfHaveOneUser = useCallback(async () => {
    try {
      setCurrentUser(users[0])
      const token = users[0]?.token
      const refreshToken = users[0]?.refreshToken
      setToken(token)
      setRefreshToken(refreshToken)
      setCache('user', users[0]?.id)

      const termsChecked = await service.checkTermsAcceptance()
      updateSetLoading(true)
      const userClinic: ClinicAndFlags = await service.getClinic(
        users[0]?.token,
      )
      const clinicColors = await themeServices.getClinicColorsById(
        userClinic?.clinic?.idClinica,
      )
      const clinicColorsRebranding =
        await rebrandedThemeServices.getClinicColorsById(
          userClinic?.clinic?.idClinica,
        )

      const ip = await service.getUserIP().catch(() => {
        console.error('Erro ao pegar ip')
        return ''
      })
      if (!users[0]?.flagAccessedBefore)
        await service.validateFirstAccess(users[0]?.token)
      choiceUser(users[0]?.id, ip)
      if (!termsChecked) {
        setTermsModalActive(true)
        showModal('TERMS_AND_CONDITIONS', {
          title: 'Termos e Condições Gerais de Uso',
          persistent: true,
        })
        return
      }
      setThemeColors(clinicColors)
      setRebrandingThemeColors(clinicColorsRebranding)
      setUserClinic(userClinic)
      setSpecialities(userClinic?.clinic)

      if (userClinic?.flags?.HabilitarAutenticacaoDoisFatoresLoginPaciente) {
        let twoFactorAuthRoute = '/autenticacao-dois-fatores'

        const uniqueCode = getCache<string>('unique-code')

        if (uniqueCode) {
          twoFactorAuthRoute += `/${uniqueCode}`
        }

        history.push(twoFactorAuthRoute, location?.state?.from)
        return
      }

      redirectAfterChoice('/')
    } catch (e) {
      toast({
        message: 'Algo de errado ocorreu. Tente novamente',
        variant: 'error',
      })
    }
  }, [
    users,
    choiceUser,
    setThemeColors,
    setRebrandingThemeColors,
    setUserClinic,
    setSpecialities,
    redirectAfterChoice,
    showModal,
    history,
    location?.state?.from,
  ])

  const setUser = useCallback(
    async (user: User) => {
      setTermsModalActive(false)
      try {
        updateSetLoading(true)
        setCurrentProfileId(user.id)
        const userClinic: ClinicAndFlags = await service.getClinic(user?.token)
        const clinicColors = await themeServices.getClinicColorsById(
          userClinic?.clinic?.idClinica,
        )
        const clinicColorsRebranding =
          await rebrandedThemeServices.getClinicColorsById(
            userClinic.clinic.idClinica,
          )
        const ip = await service.getUserIP().catch(() => {
          console.error('Erro ao pegar ip')
          return ''
        })
        if (!user?.flagAccessedBefore)
          await service.validateFirstAccess(user?.token)
        choiceUser(user.id, ip)
        if (clinicColors) {
          setThemeColors(clinicColors)
        }
        if (clinicColorsRebranding) {
          setRebrandingThemeColors(clinicColorsRebranding)
        }
        setUserClinic(userClinic)
        setSpecialities(userClinic.clinic)

        if (userClinic?.flags?.PermiteGeolocalizacao) {
          navigator.geolocation.getCurrentPosition(position => {
            const coords = position.coords
            if (coords) {
              service.sendGeolocation({
                accuracy: coords.accuracy,
                altitude: coords.altitude,
                altitudeAccuracy: coords.altitudeAccuracy,
                heading: coords.heading,
                latitude: coords.latitude,
                longitude: coords.longitude,
                speed: coords.speed,
              })
            }
          })
        }

        if (userClinic?.flags?.HabilitarAutenticacaoDoisFatoresLoginPaciente) {
          let twoFactorAuthRoute = '/autenticacao-dois-fatores'

          const uniqueCode = getCache<string>('unique-code')

          if (uniqueCode) {
            twoFactorAuthRoute += `/${uniqueCode}`
          }

          history.push(twoFactorAuthRoute, location?.state?.from)
          return
        }

        redirectAfterChoice('/')
      } catch (e) {
        console.log(e)
        setLoading(false)
        setCurrentProfileId(0)
        toast({
          message: 'Algo de errado ocorreu. Tente novamente',
          variant: 'error',
        })
      }
    },
    [
      choiceUser,
      setThemeColors,
      setRebrandingThemeColors,
      setUserClinic,
      setSpecialities,
      redirectAfterChoice,
      history,
      location?.state,
    ],
  )

  const handleUserSelect = useCallback(
    async (user: User) => {
      try {
        if (user?.origin !== 'PV') {
          await service.validateToken(user?.token)

          updateSetLoading(true)
          setCurrentProfileId(user?.id)
          setCurrentUser(user)
          setToken(user?.token)
          setRefreshToken(user?.refreshToken)
          setCache('user', user?.id)

          const termsChecked = await service.checkTermsAcceptance()
          logAnalyticsEvent('Pac_Clinicas_AcessarConta')
          if (!termsChecked) {
            setTermsModalActive(true)
            showModal('TERMS_AND_CONDITIONS', {
              title: 'Termos e Condições Gerais de Uso',
              persistent: true,
            })
            updateSetLoading(false)
            setCurrentProfileId(0)
            return
          }
          setUser(user)
        } else {
          window.location.href = user?.redirectUrl
        }
      } catch (err) {
        updateSetLoading(false)
        setCurrentProfileId(0)
        if (
          err?.response?.data?.toString() ===
            'Erro de validação na requisição no campo: token' ||
          err?.response?.data?.msg?.includes('INVALID_ACCESS_TOKEN')
        ) {
          signOut(true)
          history.push('/login')
        } else {
          showErrorToast(err)
        }
      }
    },
    [history, logAnalyticsEvent, setUser, showModal, signOut],
  )

  useEffect(() => {
    setConfirmedData(false)
    setUserActivated(false)
    if (!users) {
      history.push('/login', {
        from: location?.state?.from,
      })
    }
    if (!users?.length) {
      setTimeout(() => {
        history.push('/login', {
          from: location?.state?.from,
        })
        toast({
          message:
            'Não foi possível continuar. Entre em contato com a sua operadora de saúde ou empresa.',
          variant: 'error',
        })
      }, 2000)
    } else if (users?.length === 1) {
      setIfHaveOneUser()
    }
    if (location?.pathname === '/escolha-unificada') {
      setCache('unified-redirect-multi-profile', true)
    } else {
      removeCache('unified-redirect-multi-profile')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const changeActiveTab = useCallback(() => {
    logAnalyticsEvent('Pac_Clinicas_VerContas')
    updateSetActiveTab('pendentUsers')
  }, [logAnalyticsEvent])

  const handleInactiveUserSelect = useCallback(
    (selectedUser: User) => {
      const inactiveUserObject: InactiveUser = {
        patientId: selectedUser?.patientId,
        clinicId: selectedUser?.clinic?.id,
        adultSimpleDependents: selectedUser?.adultDependents,
        childrenSimpleDependents: selectedUser?.childrenDependents,
      }
      setInactiveUser(inactiveUserObject)
      history.push('/ativacao/inicio')
    },
    [history, setInactiveUser],
  )

  const handleShowDependentsModal = useCallback(
    (user: User) => {
      logAnalyticsEvent('Pac_AtivacaoLogada_VerDependentes')
      setCurrentUser(user)
      showModal('DEPENDENTS_MODAL', {
        title: 'Dependentes',
      })
    },
    [logAnalyticsEvent, showModal],
  )

  const activeUsers = users?.filter(user => user?.flagIsActive)
  const inactiveUsers = users?.filter(user => !user?.flagIsActive)

  const updateSetActiveTab = (activeTab: string) => {
    setActiveTab(activeTab)
  }

  const updateSetAlertIsOpen = (open: boolean) => {
    setAlertIsOpen(open)
  }

  const updateSetLoading = (loading: boolean) => {
    setLoading(loading)
  }

  return (
    <HeroWrapper>
      <RenderIf condition={!users}>
        <LoadingPage
          withoutNav={true}
          text="Estamos deixando tudo pronto pra você!"
        />
      </RenderIf>
      <RenderIf condition={users?.length === 1}>
        <div className="mt-3">
          {!termsModalActive && <LoadingPage withoutNav={true} />}
        </div>
      </RenderIf>
      <RenderIf condition={users?.length > 1}>
        <ChoiceMultiprofile
          activeTab={activeTab}
          activeUsers={activeUsers}
          alertIsOpen={alertIsOpen}
          currentProfileId={currentProfileId}
          currentUser={currentUser}
          inactiveUsers={inactiveUsers}
          loading={loading}
          termsModalActive={termsModalActive}
          users={users}
          actionBackStepper={returnToLoginPage}
          changeActiveTab={changeActiveTab}
          handleInactiveUserSelect={handleInactiveUserSelect}
          handleShowDependentsModal={handleShowDependentsModal}
          handleUserSelect={handleUserSelect}
          updateSetActiveTab={updateSetActiveTab}
          updateSetAlertIsOpen={updateSetAlertIsOpen}
          updateSetUser={setUser}
          updateSetLoading={updateSetLoading}
        />
      </RenderIf>
      <TermsAndConditionsModal
        redirectTo={''}
        directLogin={() => setUser(currentUser)}
        setLoading={updateSetLoading}
        userToken={currentUser?.token}
      />
      <DependentsModal
        adultDependents={currentUser?.adultDependents}
        childDependents={currentUser?.childrenDependents}
      />
    </HeroWrapper>
  )
}

export default Choice
